import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAllProjects } from '../services/projectService';
import { getProjects as getStaticProjects } from '../data/projects';
import { useNewProjectsIndicator } from '../hooks/useNewProjectsIndicator';

const ProjectContext = createContext();

export const useProjects = () => {
  return useContext(ProjectContext);
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Integration du système de nouveaux projets
  const {
    processProjectsForNewIndicator,
    markProjectAsNew,
    markAllProjectsAsViewed,
    getNewProjectsCount,
    cleanupOldNewProjects
  } = useNewProjectsIndicator();

  // Ces fonctions changent à chaque mise à jour des "nouveaux projets" : on les lit via une ref
  // pour que loadProjects reste stable et ne relance pas le chargement en boucle.
  const indicatorRef = useRef({ processProjectsForNewIndicator, cleanupOldNewProjects });
  indicatorRef.current = { processProjectsForNewIndicator, cleanupOldNewProjects };

  // silent : mise à jour en arrière-plan, sans repasser la page en « Chargement »
  const loadProjects = useCallback(async ({ silent = false } = {}) => {
    const { processProjectsForNewIndicator, cleanupOldNewProjects } = indicatorRef.current;
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const initialProjectsData = await getAllProjects();
      
      // Traitement des projets pour les indicateurs "nouveau"
      const processedProjects = processProjectsForNewIndicator(initialProjectsData);
      setProjects(processedProjects);
      
      // Nettoyage des anciens projets marqués comme nouveaux
      cleanupOldNewProjects(processedProjects);
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      
      try {
        const staticProjects = getStaticProjects();
        const processedStaticProjects = processProjectsForNewIndicator(staticProjects);
        setProjects(processedStaticProjects);
      } catch (staticError) {
        console.error('Erreur lors du chargement des projets statiques:', staticError);
        setError('Impossible de charger les projets');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    // Un paiement confirmé change le montant collecté et le nombre d'investisseurs des projets
    const refreshSilently = () => loadProjects({ silent: true });
    window.addEventListener('investment-updated', refreshSilently);
    return () => window.removeEventListener('investment-updated', refreshSilently);
  }, [loadProjects]);

  const updateProjectsContext = useCallback((newProjects) => {
    setProjects(newProjects);
  }, []);

  const addProject = useCallback((newProject) => {
    // Marquer automatiquement le nouveau projet comme "nouveau"
    markProjectAsNew(newProject.id);
    
    // Ajouter la propriété isNew au projet
    const projectWithNewFlag = {
      ...newProject,
      isNew: true,
      isRecentlyAdded: true,
      dateCreated: newProject.dateCreated || new Date().toISOString()
    };
    
    setProjects(prevProjects => [...prevProjects, projectWithNewFlag]);
  }, [markProjectAsNew]);

  const contextValue = useMemo(() => ({
    projects, 
    loading, 
    error,
    setProjects: updateProjectsContext,
    addProject,
    refreshProjects: loadProjects,
    
    // Fonctions pour gérer les nouveaux projets
    markAllProjectsAsViewed,
    getNewProjectsCount
  }), [projects, loading, error, updateProjectsContext, addProject, loadProjects, markAllProjectsAsViewed, getNewProjectsCount]);

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;
